
























































































import {
  defineComponent,
  ref,
  computed,
  watch,
  PropType,
  set
} from '@nuxtjs/composition-api';
import { useAlgoliaInsights, useTranslations } from '@vue-storefront/novulo';
import { addCurrency, priceFormat } from '~/helpers/utils';
import { usePriceFilter } from '~/composables/usePriceFilter';

type FilterOption = {
  _id: number;
  name: string;
  number_of_products: number;
  selected?: boolean;
};

type SelectedFilter = {
  filterId: string;
  value: string;
};

type FilterType = {
  filter_id: string;
  filter_name: string;
  filter_explanation: string;
  values: FilterOption[];
};

export default defineComponent({
  emits: [
    'filters:update',
    'filters:count',
    'closeMobileFilters',
    'filters:track'
  ],
  name: 'ProductListingFilters',
  props: {
    activeFacetFilters: { type: Array as PropType<string[][]>, default: () => [] },
    activeNumericFilters: { type: Array as PropType<string[]>, default: () => [] },
    cmsEssentials: { type: Object as PropType<string>, required: true },
    facets: { type: Object, required: false, default: () => ({}) },
    facetsStats: { type: Object, required: false, default: () => ({}) },
    totalCount: { type: Number, default: 0 }
  },
  setup(props, { emit }) {
    // const filterBrands = (props.cmsEssentials as any)?.filter_brands ?? [];
    // @todo fein move this to upper method
    const { trackFilterClick } = useAlgoliaInsights();
    const { translate, hasTranslation } = useTranslations();

    const cmsFilters = (props.cmsEssentials as any).filters;
    const mergedFilters = ref([]);
    const price = usePriceFilter({ min: 0, max: 1000000 });

    const formattedPriceChips = computed(() =>
      price.state.touched
        ? [{
          name: `${addCurrency(priceFormat(price.state.selected.min, { hideFractionIfInt: true }))}
               - ${addCurrency(priceFormat(price.state.selected.max, { hideFractionIfInt: true }))}`,
          value: `${price.state.selected.min}-${price.state.selected.max}`,
          min: price.state.selected.min,
          max: price.state.selected.max
        }]
        : []);

    watch(
      () => props.facets,
      (newFacets) => {
        if (!newFacets || Object.keys(newFacets).length === 0) return;

        // compute merged filters, if not present
        if (mergedFilters.value.length === 0) {
          mergedFilters.value = cmsFilters
            .map((f) => {
              const counts = newFacets[f.filter_id] || {};

              const values = Object.entries(counts).map(([label, count]) => {
                // check URL-driven “facetFilters” prop:
                const isSelected = props.activeFacetFilters.some(group =>
                  group.some(str => {
                    const [attr, val] = str.split(':');
                    return attr === f.filter_id && val === label;
                  })
                );

                return {
                  _id: f.filter_id,
                  name: label,
                  number_of_products: count as number,
                  selected: isSelected
                };
              });

              return {
                id: f.id,
                filter_id: f.filter_id,
                filter_name: f.filter_name,
                filter_type_title: f.filter_type_title,
                filter_explanation: f.filter_explanation,
                filter_detailed_explanation: f.filter_detailed_explanation,
                values: values.filter(v => v.number_of_products > 0 || v.selected)
              };
            }).filter(f => f.values.length > 0);

        } else {
          // mergedFilters exists, update the number of products
          mergedFilters.value.forEach((filter) => {
            const counts = newFacets[filter.filter_id] || {};

            filter.values.forEach((value) => {
              value.number_of_products = counts[value.name] || 0;
            });
          });
        }
      },
      { deep: true, immediate: true }
    );

    // set initial min / max price from Algolia
    watch(
      // () => props.facetsStats?.priceAfterDiscount, @todo fein fix this with Jan
      () => props.facetsStats?.priceAfterDiscount,
      stats => {
        if (!stats) return;

        const boundsGotWider = stats.min < price.state.absolute.min || stats.max > price.state.absolute.max;

        if (!price.state.touched || boundsGotWider) {
          price.setAbsolute(stats.min, stats.max);
        }
      },
      { immediate: true }
    );

    // set active price from URL
    watch(
      () => props.activeNumericFilters,
      filters => price.hydrate(filters),
      { immediate: true }
    );

    function removePriceChip() {
      price.setAbsolute(price.state.absolute.min, price.state.absolute.max);
      price.state.touched = false;
      emitFilters('numeric');
    }

    const visibleFilters = computed<FilterType[]>(() =>
      mergedFilters.value.filter(
        (filter) =>
          filter.values.some(
            (value) => value.number_of_products > 0 || value.selected === true
          ) || filter.values.some((value) => value.selected)
      )
    );

    const selectedFilters = computed<SelectedFilter[]>(() =>
      visibleFilters.value.flatMap((filter) =>
        filter.values
          .filter((v) => Boolean(v.selected))
          .map((v) => ({
            filterId: filter.filter_id,
            value: v.name
          }))
      )
    );

    function updatePrice(range: { min: number; max: number }) {
      price.setSelected(range.min, range.max);
      emitFilters('numeric');
    }

    function noFiltersLeft(): boolean {
      return selectedFilters.value.length === 0 && !price.state.touched;
    }

    // whenever anything changes, re‐emit one single payload
    function emitFilters(type = 'facet') {
      const payload = {
        activeFilters: selectedFilters.value,
        priceRange: price.state.touched ? price.state.selected : null,
        type
      };

      emit('filters:update', payload);
    }

    function updateFilter(
      option: { optionId: string; name: string; selected: boolean },
      filter: FilterType
    ) {
      trackFilterClick({
        filter: filter.filter_name,
        category: option.name
      });

      emit('filters:track', {
        filter: filter.filter_name,
        category: option.name,
        selected: option.selected
      });

      // toggle selected
      const filterValue = filter.values.find((v) => v.name === option.name);
      if (filterValue) {
        set(filterValue, 'selected', option.selected);
      }

      if (noFiltersLeft()) {
        return removeAllFilters();
      }

      emitFilters();
    }

    function removeSelectedFilter(filterToRemove: SelectedFilter) {
      const group = mergedFilters.value.find(
        (f) => f.filter_id === filterToRemove.filterId
      );

      if (group) {
        const option = group.values.find(
          (v) => v.name === filterToRemove.value
        );
        if (option) {
          set(option, 'selected', false);
        }
      }

      if (noFiltersLeft()) {
        return removeAllFilters();
      }

      emitFilters();
    }

    function removeAllFilters() {
      // reset checkbox filters
      mergedFilters.value.forEach((f) =>
        f.values.forEach((v) => (v.selected = false))
      );

      // reset price filters
      price.setAbsolute(price.state.absolute.min, price.state.absolute.max);
      price.state.touched = false;

      emitFilters();
    }

    const titleForFilter = (filter) => {
      const nameKey = filter.filter_name;
      const explanationKey = filter.filter_explanation;

      return {
        name: hasTranslation(nameKey)
          ? translate(nameKey)
          : nameKey,
        explanation: hasTranslation(explanationKey)
          ? translate(explanationKey)
          : (explanationKey.startsWith('filter_explanation') ? '' : explanationKey)
      };
    };

    return {
      formattedPriceChips,
      mergedFilters,
      selectedFilters,
      visibleFilters,
      price,
      removeAllFilters,
      removePriceChip,
      removeSelectedFilter,
      titleForFilter,
      updateFilter,
      updatePrice
    };
  }
});
